import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { AlertCircle, Bell, CheckCircle2, FileDown, FileUp, Info, RefreshCw, Trash2, UserPlus } from 'lucide-react';

import { Button } from "@/components/ui/button";
import type { Notification } from '@/mocks/notifications';

const getNotificationIcon = (type: string, icon?: string) => {
    if (icon) {
        switch (icon) {
            case 'user-plus':
                return <UserPlus className="h-4 w-4" />
            case 'file-export':
                return <FileDown className="h-4 w-4" />
            case 'file-import':
                return <FileUp className="h-4 w-4" />
            case 'update':
                return <RefreshCw className="h-4 w-4" />
            default:
                return <Bell className="h-4 w-4" />
        }
    }

    switch (type) {
        case 'success':
            return <CheckCircle2 className="h-4 w-4 text-green-500" />
        case 'warning':
            return <AlertCircle className="h-4 w-4 text-yellow-500" />
        case 'info':
            return <Info className="h-4 w-4 text-blue-500" />
        default:
            return <Bell className="h-4 w-4 text-gray-500" />
    }
}

const getNotificationColor = (type: string) => {
    switch (type) {
        case 'success':
            return 'text-green-500'
        case 'warning':
            return 'text-yellow-500'
        case 'info':
            return 'text-blue-500'
        case 'error':
            return 'text-red-500'
        default:
            return 'text-gray-500'
    }
}

type NotificationTimelineProps = {
    notifications: Notification[];
    isLoading?: boolean;
    onDelete?: (id: number) => void;
    formatDate: (date: Date) => string;
};

export default function NotificationTimeline({ notifications, isLoading = false, onDelete, formatDate }: NotificationTimelineProps) {
    if (isLoading) {
        return (
            <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
                <div className="space-y-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="relative pl-12 animate-pulse">
                            <div className="absolute left-0 w-10 h-10 rounded-full bg-gray-200" />
                            <div className="space-y-2">
                                <div className="h-4 w-3/4 bg-gray-200 rounded" />
                                <div className="h-3 w-1/2 bg-gray-200 rounded" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
            <div className="space-y-6">
                {notifications.map((notification) => (
                    <div key={notification.id} className="relative pl-12 group">
                        <div className="absolute left-0">
                            <div className={`h-10 w-10 rounded-full bg-gray-100 border-2 border-background flex items-center justify-center ${getNotificationColor(notification.type)}`}>
                                {getNotificationIcon(notification.type, notification.icon)}
                            </div>
                        </div>
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-medium">
                                        {notification.title}
                                    </h3>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {notification.message}
                                </p>
                                {notification.action?.details && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {notification.action.details}
                                    </p>
                                )}
                                <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs text-muted-foreground">
                                        {formatDate(notification.date)} • {format(notification.date, 'HH:mm', { locale: nl })}
                                    </span>
                                </div>
                            </div>
                            {onDelete && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={() => onDelete(notification.id)}
                                >
                                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
} 